import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  type ModalProps,
  useDisclosure,
} from '@heroui/react';

interface DialogProps extends ModalProps {
  trigger: (onOpen: () => void) => React.ReactNode;
  children: React.ReactNode;
  footer?: (onClose: () => void) => React.ReactNode;
  title: string;
  onOpenChange?: (isOpen: boolean) => void;
  id?: string;
  footerClassName?: string;
  showHeader?: boolean;
}

export const Dialog = ({
  trigger,
  children,
  footer,
  title,
  isOpen,
  onOpenChange,
  id,
  footerClassName,
  showHeader = true,
  ...props
}: DialogProps) => {
  const { isOpen: open, onOpenChange: toggleOpen } = useDisclosure({
    isOpen,
    onChange: onOpenChange,
    id,
  });

  // Refactored for clarity and to avoid passing arguments to toggleOpen (which expects none)
  const handleOpenChange = (nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    } else {
      toggleOpen();
    }
  };

  const handleOpen = () => handleOpenChange(true);
  const handleClose = () => handleOpenChange(false);

  return (
    <>
      {trigger(handleOpen)}
      <Modal isOpen={open} onOpenChange={handleOpenChange} onClose={handleClose} {...props}>
        <ModalContent>
          {(onClose) => (
            <>
              {showHeader && (
                <ModalHeader className='flex flex-col gap-1 border-b border-default-200'>
                  <h3 className='text-lg font-semibold text-default-800'>{title}</h3>
                </ModalHeader>
              )}
              <ModalBody>{children}</ModalBody>
              {footer && <ModalFooter className={footerClassName}>{footer?.(onClose)}</ModalFooter>}
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
